import type { WeatherCard, WeatherDay, WeatherHour } from '../../api/types'
import {
  FeuchteBild,
  KompassBild,
  MondBild,
  OrtungBild,
  SonnenaufgangBild,
  SonnenuntergangBild,
  ThermometerBild,
  Wetterbild,
  WindBild,
} from '../../components/Wetterbilder'
import { datumText, stundeVon, uhrText, type Versatz } from '../../lib/ortszeit'
import { beschreibe, himmelsrichtung, lageVon, temperaturFarbe } from '../../lib/wetterlage'
import { WetterNennung } from '../map/Wetter'
import { useTexte } from '../../texte'

/**
 * The weather card in the chat (0.31.0; since 0.33.0 built like the weather
 * widget on the iPhone -- from use, as a screenshot of the two widgets).
 *
 * **Since 0.37.0 it is a short card** -- from use: "Wetter sieht gut aus,
 * aber die Kachel ist zu groß." Top the head: place, temperature, the sign
 * with high and low. Then the hours ahead, a whole day of them. Then one
 * row for today: sign, chance of rain, low, the bar, high -- the first row
 * of the day list of the Weather app, and only that one. Then three square
 * tiles: wind with the compass, humidity with the felt temperature, the
 * moon. The days ahead, the sun, UV, visibility, pressure and clouds are
 * gone from the card; the server still stores them, and the book prints
 * what it prints.
 *
 * The face and the type are tokens (`--wetter-…`), the signs and the bar's
 * colours are pictures and data (`Wetterbilder.tsx`, `temperaturFarbe()`).
 * **What is missing is left out**, not shown as zero. Moments are shown in
 * **the card's own zone** (1.86.0; the trip's until then) -- the zone of the
 * tap, or the one the chosen hour was typed in, for a reader anywhere.
 * Apple Weather has to be named; the link is the same one as under a
 * highlight.
 */
export function Wetterkarte({ karte }: { karte: WeatherCard }) {
  const t = useTexte().wetterkarte
  const versatz: Versatz = karte.tz_offset_min
  const grad = karte.temperature_c === null ? null : Math.round(karte.temperature_c)
  const lage = karte.condition === null ? null : lageVon(karte.condition)
  const wort = karte.condition === null ? null : beschreibe(karte.condition)
  const mondwort = karte.moon_phase === null ? null : (t.mondphasen[karte.moon_phase] ?? karte.moon_phase)
  const rest = [karte.region, karte.country].filter((s) => s !== '')
  const mondzeiten = t.mondZeiten(
    karte.moonrise_at === null ? null : uhrText(karte.moonrise_at, versatz),
    karte.moonset_at === null ? null : uhrText(karte.moonset_at, versatz),
  )

  return (
    <div className={karte.daylight === false ? 'wetterkarte wetterkarte--nacht' : 'wetterkarte'} data-wetterkarte={karte.id} data-nacht={karte.daylight === false ? '' : undefined}>
      <div className="wetterkarte__kopf">
        <div className="wetterkarte__links">
          {karte.place !== '' && (
            <span className="wetterkarte__ort">
              {karte.place}
              {/* The arrow of the widget: this is where the device stood. A
                  chosen place (#83) has no arrow; its moment stands below. */}
              {karte.located && <OrtungBild size={14} title={t.standort} />}
            </span>
          )}
          {grad !== null && <span className="wetterkarte__grad">{t.grad(grad)}</span>}
          {/* The moment, on both kinds (1.86.0, from use: "die Karte des
              aktuellen Wetters zeigt keine Uhrzeit, lokale Uhrzeit ist
              relevant"). A located card says the hour alone, a chosen one
              that it was chosen. */}
          {karte.observed_at !== null && (
            <span className="wetterkarte__wann" data-wann={karte.observed_at} data-gewaehlt={karte.located ? undefined : karte.observed_at}>
              {karte.located
                ? `${datumText(karte.observed_at, versatz)}, ${uhrText(karte.observed_at, versatz)}`
                : t.gewaehlt(`${datumText(karte.observed_at, versatz)}, ${uhrText(karte.observed_at, versatz)}`)}
            </span>
          )}
        </div>
        <div className="wetterkarte__rechts">
          <Wetterbild lage={lage} daylight={karte.daylight} size={34} title={wort ?? undefined} />
          {wort !== null && <span className="wetterkarte__lage">{wort}</span>}
          {karte.temp_max_c !== null && (
            <span className="wetterkarte__extrem" title={t.hoechst}>↑{t.grad(Math.round(karte.temp_max_c))}<span className="visually-hidden">{t.hoechst}</span></span>
          )}
          {karte.temp_min_c !== null && (
            <span className="wetterkarte__extrem wetterkarte__extrem--tief" title={t.tiefst}>↓{t.grad(Math.round(karte.temp_min_c))}<span className="visually-hidden">{t.tiefst}</span></span>
          )}
        </div>
      </div>
      <Stunden
        stunden={karte.hours ?? []}
        sonnen={[
          ...(karte.sunrise_at === null ? [] : [{ iso: karte.sunrise_at, aufgang: true }]),
          ...(karte.sunset_at === null ? [] : [{ iso: karte.sunset_at, aufgang: false }]),
          ...(karte.forecast ?? []).flatMap((d) => [
            ...(d.sunrise === null || d.sunrise === undefined ? [] : [{ iso: d.sunrise, aufgang: true }]),
            ...(d.sunset === null || d.sunset === undefined ? [] : [{ iso: d.sunset, aufgang: false }]),
          ]),
        ]}
        versatz={versatz}
        jetzt={karte.located}
      />
      <Heute tage={karte.forecast ?? []} jetzt={karte.temperature_c} name={karte.located || karte.observed_at === null ? t.heute : datumText(karte.observed_at, versatz)} />
      <div className="wetterkarte__kacheln">
        {karte.wind_kmh !== null && (
          <section className="wetterkarte__kachel" data-kachel="wind">
            <h3 className="wetterkarte__kachel-kopf"><WindBild size={16} />{t.wind}</h3>
            {karte.wind_dir_deg !== null ? (
              <KompassBild degrees={karte.wind_dir_deg} speed={t.kmh(Math.round(karte.wind_kmh))} letters={t.himmel} size={52} title={t.richtungWert(karte.wind_dir_deg, himmelsrichtung(karte.wind_dir_deg))} />
            ) : (
              <p className="wetterkarte__gross">{t.kmh(Math.round(karte.wind_kmh))}</p>
            )}
            {karte.gust_kmh !== null && <p className="wetterkarte__klein wetterkarte__klein--zeile">{t.boeenWert(t.kmh(Math.round(karte.gust_kmh)))}</p>}
            <span className="visually-hidden">
              {t.kmh(Math.round(karte.wind_kmh))}
              {karte.wind_dir_deg !== null && `, ${t.richtungWert(karte.wind_dir_deg, himmelsrichtung(karte.wind_dir_deg))}`}
            </span>
          </section>
        )}
        {/* The middle tile: humidity and the felt temperature, two values and
            no third. 0.39.0 tried three (visibility) and from use came: "am
            Smartphone ist kein Platz mehr" -- 110 points hold two pairs at a
            readable size, not three. */}
        {(karte.humidity_pct !== null || karte.feels_like_c !== null) && (
          <section className="wetterkarte__kachel" data-kachel="luftfeuchte">
            {karte.humidity_pct !== null && (
              <>
                <h3 className="wetterkarte__kachel-kopf"><FeuchteBild size={16} />{t.luftfeuchte}</h3>
                <p className="wetterkarte__gross">{t.prozent(karte.humidity_pct)}</p>
              </>
            )}
            {karte.feels_like_c !== null && (
              <>
                <h3 className="wetterkarte__kachel-kopf"><ThermometerBild size={16} />{t.gefuehlt}</h3>
                <p className="wetterkarte__gross">{t.grad(Math.round(karte.feels_like_c))}</p>
              </>
            )}
          </section>
        )}
        {mondwort !== null && (
          <section className="wetterkarte__kachel" data-kachel="mond">
            <h3 className="wetterkarte__kachel-kopf">{mondwort}</h3>
            <MondBild phase={karte.moon_phase} size={40} className="wetterkarte__mond" />
            {mondzeiten !== '' && <p className="wetterkarte__klein wetterkarte__klein--zeile">{mondzeiten}</p>}
          </section>
        )}
      </div>
      <p className="wetterkarte__fuss">
        {rest.length > 0 && `${rest.join(t.trenner)}${t.trenner}`}
        <WetterNennung className="wetterkarte__quelle" />
      </p>
    </div>
  )
}

/**
 * The hours ahead (0.34.0; a whole day of them since 0.37.0), like the row
 * at the top of the Weather app: "Jetzt", then the hours with sign and
 * temperature; sunrise and sunset take their place in between -- today's
 * and those of the days ahead, whichever fall into the span (0.38.0: with
 * a whole day of hours, the next sunrise is tomorrow's). The row scrolls
 * inside the card.
 */
function Stunden({ stunden, sonnen, versatz, jetzt }: { stunden: WeatherHour[]; sonnen: { iso: string; aufgang: boolean }[]; versatz: Versatz; jetzt: boolean }) {
  const t = useTexte().wetterkarte
  const hours = stunden.filter((h) => h.time !== null && h.temperature_c !== null)
  if (hours.length < 2) return null
  type Entry = { at: number; key: string; wann: string; bild: React.ReactNode; wert: string; sonne: boolean; hidden: string }
  const entries: Entry[] = hours.map((h, i) => {
    const at = new Date(h.time as string).getTime()
    const stunde = stundeVon(h.time as string, versatz)
    // "Jetzt" only where the first hour is now; on a card about a chosen hour it is that hour.
    const wann = i === 0 && jetzt ? t.jetzt : stunde === null ? '' : t.stunde(stunde)
    const lage = h.condition === null ? null : lageVon(h.condition)
    const wort = h.condition === null ? null : beschreibe(h.condition)
    const grad = Math.round(h.temperature_c as number)
    return { at, key: `h${i}`, wann, bild: <Wetterbild lage={lage} daylight={null} size={26} />, wert: t.grad(grad), sonne: false, hidden: t.stundeZeile(wann, wort, grad) }
  })
  const first = entries[0]?.at ?? 0
  const last = entries[entries.length - 1]?.at ?? 0
  const gesehen = new Set<number>()
  for (const { iso, aufgang } of sonnen) {
    const at = new Date(iso).getTime()
    if (Number.isNaN(at) || at < first || at > last || gesehen.has(at)) continue
    gesehen.add(at)
    const name = aufgang ? t.sonnenaufgang : t.sonnenuntergang
    entries.push({ at, key: `s${at}`, wann: uhrText(iso, versatz), bild: aufgang ? <SonnenaufgangBild size={26} /> : <SonnenuntergangBild size={26} />, wert: name, sonne: true, hidden: `${name} ${uhrText(iso, versatz)}` })
  }
  entries.sort((a, b) => a.at - b.at)
  return (
    <ol className="wetterkarte__stunden" aria-label={t.stunden} data-stunden={hours.length}>
      {entries.map((e) => (
        <li key={e.key} className={e.sonne ? 'wetterkarte__stunde wetterkarte__stunde--sonne' : 'wetterkarte__stunde'} data-stunde={e.sonne ? 'sonne' : ''}>
          <span className="wetterkarte__stunde-wann" aria-hidden="true">{e.wann}</span>
          {e.bild}
          <span className="wetterkarte__stunde-wert" aria-hidden="true">{e.wert}</span>
          <span className="visually-hidden">{e.hidden}</span>
        </li>
      ))}
    </ol>
  )
}

/**
 * Today, like the first row of the day list in the Weather app: "Heute",
 * the sign with the chance of rain beneath it, low, the bar with a dot for
 * the temperature right now, high.
 *
 * **The bar is measured on the week's span**, not on today's alone -- on
 * its own span every day would fill the bar from edge to edge and say
 * nothing. The days after today are what the server still stores them for
 * (0.37.0); shown is only this one row.
 */
function Heute({ tage, jetzt, name }: { tage: WeatherDay[]; jetzt: number | null; name: string }) {
  const t = useTexte().wetterkarte
  const mitWerten = tage.filter((d) => d.temp_min_c !== null && d.temp_max_c !== null)
  const heute = mitWerten[0]
  if (heute === undefined) return null
  const tief = Math.min(...mitWerten.map((d) => d.temp_min_c as number))
  const hoch = Math.max(...mitWerten.map((d) => d.temp_max_c as number))
  const spanne = Math.max(1, hoch - tief)
  const min = heute.temp_min_c as number
  const max = heute.temp_max_c as number
  const regen = heute.precip_chance_pct !== null && heute.precip_chance_pct >= 30 ? heute.precip_chance_pct : null
  const lage = heute.condition === null ? null : lageVon(heute.condition)
  const wort = heute.condition === null ? null : beschreibe(heute.condition)
  return (
    <div className="wetterkarte__tag" data-tag={name}>
      <span className="wetterkarte__tag-name" aria-hidden="true">{name}</span>
      <span className="wetterkarte__tag-bild" aria-hidden="true">
        <Wetterbild lage={lage} daylight={null} size={22} />
        {regen !== null && <span className="wetterkarte__regen">{t.prozent(regen)}</span>}
      </span>
      <span className="wetterkarte__tief" aria-hidden="true">{t.grad(Math.round(min))}</span>
      <span className="wetterkarte__balken" aria-hidden="true">
        <span
          style={{
            left: `${((min - tief) / spanne) * 100}%`,
            width: `${Math.max(6, ((max - min) / spanne) * 100)}%`,
            background: `linear-gradient(to right, ${temperaturFarbe(min)}, ${temperaturFarbe(max)})`,
          }}
        />
        {jetzt !== null && (
          <span className="wetterkarte__jetzt" data-jetzt="" style={{ left: `${Math.max(0, Math.min(100, ((jetzt - tief) / spanne) * 100))}%` }} />
        )}
      </span>
      <span className="wetterkarte__hoch" aria-hidden="true">{t.grad(Math.round(max))}</span>
      <span className="visually-hidden">{t.tagZeile(name, wort, Math.round(min), Math.round(max))}{regen === null ? '' : `, ${t.regenchance(regen)}`}</span>
    </div>
  )
}
